// ============================================================
// MANIC MEETER — Persistence (Server-authoritative, role-keyed)
// ============================================================

import {
  MetaProgression, StatUpgradeId, PowerupId,
  STAT_UPGRADE_SPECS, POWERUP_SPECS, PermanentUnlockId,
  PERMANENT_UNLOCK_SPECS, EndingType,
} from "./types";
import { saveMetaToServer, loadMetaFromServer } from "./api";

// Active role for the current session — set once at game start
let activeRole = "";

/** Set the active player role for this session's save/load calls. */
export function setActiveRole(role: string): void {
  activeRole = role;
}

function defaultMeta(): MetaProgression {
  return {
    impostorPoints: 0,
    totalPointsEarned: 0,
    statLevels: {
      [StatUpgradeId.Gehalt]: 0,
      [StatUpgradeId.MaxMuedigkeit]: 0,
      [StatUpgradeId.Unauffaelligkeit]: 0,
      [StatUpgradeId.CriticalHit]: 0,
      [StatUpgradeId.StandingDesk]: 0,
      [StatUpgradeId.VPN]: 0,
      [StatUpgradeId.Ohrstoepsel]: 0,
      [StatUpgradeId.Kreditkarte]: 0,
    },
    powerupInventory: {
      [PowerupId.Kaffee]: 0,
      [PowerupId.HandyAkku]: 0,
      [PowerupId.NoiseCancelling]: 0,
      [PowerupId.FakeNotizen]: 0,
      [PowerupId.LaptopTarnung]: 0,
      [PowerupId.PizzaBestellen]: 0,
      [PowerupId.MuteButton]: 0,
      [PowerupId.RemoteJoker]: 0,
      [PowerupId.BullshitBingo]: 0,
      [PowerupId.PromptBibliothek]: 0,
      [PowerupId.KiZusammenfassung]: 0,
      [PowerupId.KiAgent]: 0,
    },
    permanentUnlocks: {
      [PermanentUnlockId.Firmenwagen]: false,
      [PermanentUnlockId.BueroHund]: false,
      [PermanentUnlockId.KantineFlatrate]: false,
      [PermanentUnlockId.GoldeneBadge]: false,
      [PermanentUnlockId.Homeoffice]: false,
      [PermanentUnlockId.Dienstwohnung]: false,
    },
    totalBullshitBingosUsed: 0,
    endingsUnlocked: [],
  };
}

function parseMetaFromRaw(parsed: Partial<MetaProgression>): MetaProgression {
  const meta = defaultMeta();
  if (typeof parsed.impostorPoints === "number") meta.impostorPoints = parsed.impostorPoints;
  if (typeof parsed.totalPointsEarned === "number") meta.totalPointsEarned = parsed.totalPointsEarned;
  if (parsed.statLevels) {
    for (const key of Object.values(StatUpgradeId)) {
      if (typeof parsed.statLevels[key] === "number") meta.statLevels[key] = parsed.statLevels[key];
    }
  }
  if (parsed.powerupInventory) {
    for (const key of Object.values(PowerupId)) {
      if (typeof parsed.powerupInventory[key] === "number") meta.powerupInventory[key] = parsed.powerupInventory[key];
    }
  }
  if (parsed.permanentUnlocks) {
    for (const key of Object.values(PermanentUnlockId)) {
      if (typeof parsed.permanentUnlocks[key] === "boolean") meta.permanentUnlocks[key] = parsed.permanentUnlocks[key];
    }
  }
  if (typeof parsed.totalBullshitBingosUsed === "number") meta.totalBullshitBingosUsed = parsed.totalBullshitBingosUsed;
  if (Array.isArray(parsed.endingsUnlocked)) {
    meta.endingsUnlocked = parsed.endingsUnlocked.filter(
      (e) => Object.values(EndingType).includes(e as EndingType)
    ) as EndingType[];
  }
  return meta;
}

/** Load meta for the active role from server (DB). Falls back to default on error. */
export async function loadMeta(): Promise<MetaProgression> {
  try {
    const res = await loadMetaFromServer(activeRole);
    if (res.ok && res.data?.game_state) {
      return parseMetaFromRaw(res.data.game_state as Partial<MetaProgression>);
    }
  } catch {
    // Server unreachable — start fresh
  }
  return defaultMeta();
}

/** Save meta for the active role to server only. */
export function saveMeta(meta: MetaProgression): void {
  saveMetaToServer(meta, activeRole).catch(() => {});
}

export function getUpgradeCost(upgradeId: StatUpgradeId, currentLevel: number): number {
  const def = STAT_UPGRADE_SPECS.find((u) => u.id === upgradeId)!;
  return def.baseCost + currentLevel * def.costScaling;
}

export function canAffordUpgrade(meta: MetaProgression, upgradeId: StatUpgradeId): boolean {
  const def = STAT_UPGRADE_SPECS.find((u) => u.id === upgradeId)!;
  const level = meta.statLevels[upgradeId];
  if (level >= def.maxLevel) return false;
  return meta.impostorPoints >= getUpgradeCost(upgradeId, level);
}

export function purchaseUpgrade(meta: MetaProgression, upgradeId: StatUpgradeId): boolean {
  if (!canAffordUpgrade(meta, upgradeId)) return false;
  const cost = getUpgradeCost(upgradeId, meta.statLevels[upgradeId]);
  meta.impostorPoints -= cost;
  meta.statLevels[upgradeId]++;
  saveMeta(meta);
  return true;
}

export function canAffordPowerup(meta: MetaProgression, powerupId: PowerupId): boolean {
  const def = POWERUP_SPECS.find((p) => p.id === powerupId)!;
  return meta.impostorPoints >= def.cost;
}

export function purchasePowerup(meta: MetaProgression, powerupId: PowerupId): boolean {
  if (!canAffordPowerup(meta, powerupId)) return false;
  const def = POWERUP_SPECS.find((p) => p.id === powerupId)!;
  meta.impostorPoints -= def.cost;
  meta.powerupInventory[powerupId]++;
  saveMeta(meta);
  return true;
}

export function awardPoints(meta: MetaProgression, points: number): void {
  meta.impostorPoints += points;
  meta.totalPointsEarned += points;
  saveMeta(meta);
}

export function canAffordPermanentUnlock(meta: MetaProgression, unlockId: PermanentUnlockId): boolean {
  if (meta.permanentUnlocks[unlockId]) return false;
  const def = PERMANENT_UNLOCK_SPECS.find((u) => u.id === unlockId)!;
  return meta.totalPointsEarned >= def.cost;
}

export function purchasePermanentUnlock(meta: MetaProgression, unlockId: PermanentUnlockId): boolean {
  if (!canAffordPermanentUnlock(meta, unlockId)) return false;
  meta.permanentUnlocks[unlockId] = true;
  saveMeta(meta);
  return true;
}

export function unlockEnding(meta: MetaProgression, ending: EndingType): void {
  if (!meta.endingsUnlocked.includes(ending)) {
    meta.endingsUnlocked.push(ending);
    saveMeta(meta);
  }
}

// ---- Save/Load full game state (for mid-run persistence) ----

const SAVE_KEY = "manic-meeter-savegame";

export function saveGameState(data: Record<string, unknown>): void {
  try {
    localStorage.setItem(`${SAVE_KEY}-${activeRole}`, JSON.stringify(data));
  } catch {
    // Storage full or unavailable
  }
}

export function loadGameState(): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem(`${SAVE_KEY}-${activeRole}`);
    if (!raw) return null;
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function clearGameState(): void {
  try {
    localStorage.removeItem(`${SAVE_KEY}-${activeRole}`);
  } catch {
    // Ignore
  }
}
